import { AfterViewInit, Component, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../services/auth.service';
import { HttpClient } from '@angular/common/http';
import { MatCheckboxChange, MatDialog, MatPaginator, MatSlideToggle } from '@angular/material';
import { RegulationsService } from '../../services/regulations.service';
import { RegulationIface } from '../../interfaces/regulation.iface';
import { ToastsManager } from 'ng2-toastr';
import * as FileSaver from 'file-saver';
import { RESTDataSource } from '../../helpers/rest.datasource';
import { filter, switchMap, tap } from 'rxjs/operators';
import { MatSlideToggleChange } from '@angular/material/typings/esm5/slide-toggle';
import { Subscription } from 'rxjs/Subscription';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ng2-cookies';
import { ConfirmationDialogComponent } from '../../shared/components/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-regulations',
  templateUrl: './regulations.component.html',
  styleUrls: ['./regulations.component.scss']
})
export class RegulationsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator)
  paginator: MatPaginator;
  @ViewChild('archiveToggl')
  toggl: MatSlideToggle;
  public dataSource: RESTDataSource<object>;
  public filePondOption: {};
  public regulations: RegulationIface[] | object[] = [];
  public displayedColumns = ['download', 'Key', 'LastModified', 'remove'];
  public selectedRegulations: RegulationIface[] = [];
  public isLoadingResults = false;
  public isArchiveDisplay = false;
  public archiveDirectoryName = 'archive-regulations';
  public regulationDirectoryName = 'regulations';
  public directory = '';
  public subscription: Subscription = new Subscription();
  public fileName = '';

  constructor(
    public authService: AuthService,
    private httpClient: HttpClient,
    private toastr: ToastsManager,
    private dialog: MatDialog,
    private zone: NgZone,
    private regulationsService: RegulationsService,
    private cookieService: CookieService,
    public translate: TranslateService
  ) {
    this.filePondOption = this.getUploadConfig();
  }

  ngOnInit() {
    this.regulationsService.directorySubject.next(this.regulationDirectoryName);
    this.subscription.add(
      this.regulationsService.directory$.subscribe(directory => {
        this.directory = directory;
      })
    );

    this.paginator.pageSize = 10;
    this.dataSource = new RESTDataSource<object>(this.regulationsService);
    this.loadData(this.directory, this.fileName);
    this.subscription.add(
      this.dataSource.loading$.subscribe(isLoading => {
        this.isLoadingResults = isLoading;
        if (!isLoading) {
          this.regulations = this.dataSource.getDataSource();
        }
      })
    );
  }

  ngAfterViewInit() {
    this.paginator.page
      .pipe(
        tap(() => {
          if (this.fileName) {
            this.loadData(this.directory, this.fileName);
          } else {
            if (!this.isArchiveDisplay) {
              this.loadData(this.directory, this.fileName);
            } else {
              this.loadData(this.archiveDirectoryName, this.fileName);
            }
          }
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  filterRegulations(event): void {
    this.fileName = event.target.value;
    this.fileName ? this.loadData(this.directory, this.fileName) : this.loadData(this.directory, '');
  }

  isSelected(regulation: RegulationIface): boolean {
    return this.selectedRegulations.some(selectedRegulation => {
      return selectedRegulation.name === regulation.name;
    });
  }

  loadData(directory: string, name: string): void {
    this.zone.run(() => {
      this.dataSource.loadData(
        { directory: directory, name: name },
        this.paginator.pageIndex,
        this.paginator.pageSize,
        ''
      );
    });
  }

  loadDataFromDirectory(event: MatSlideToggleChange): void {
    if (event.checked) {
      this.regulationsService.directorySubject.next(this.archiveDirectoryName);
      this.paginator.pageIndex = 0;
      this.loadData(this.directory, this.fileName);
      this.selectedRegulations = [];
      this.isArchiveDisplay = true;
    } else {
      this.regulationsService.directorySubject.next(this.regulationDirectoryName);
      this.loadData(this.directory, this.fileName);
      this.isArchiveDisplay = false;
    }
    this.selectedRegulations = [];
  }

  selectRegulations(event: MatCheckboxChange, regulation: RegulationIface): RegulationIface[] {
    if (event.checked) {
      this.selectedRegulations.push(regulation);
      return this.selectedRegulations;
    }
    return this.selectedRegulations.splice(this.selectedRegulations.indexOf(regulation), 1);
  }

  archiveRegulations(): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'REGULATIONS.ARCHIVE_DIALOG.TITLE'
        }
      })
      .afterClosed()
      .pipe(
        filter(v => v),
        switchMap(() => {
          const regulationsName = this.selectedRegulations.map(regulation => {
            return regulation.name;
          });

          return this.regulationsService.changeBucketFolder(regulationsName);
        })
      )
      .subscribe(
        () => {
          this.toastr.success(this.translate.instant('REGULATIONS.COMPONENT.ARCHIVING_SUCCESS'));
          this.loadData(this.directory, this.fileName);
          this.selectedRegulations = [];
        },
        () => this.toastr.error(this.translate.instant('REGULATIONS.COMPONENT.ARCHIVING_FAILED'))
      );
  }

  deleteRegulation(regulation: RegulationIface): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'REGULATIONS.DELETE_ONE_DIALOG.TITLE',
          body: 'REGULATIONS.DELETE_ONE_DIALOG.BODY',
          translateParams: {
            regulationName: regulation.name
          }
        }
      })
      .afterClosed()
      .pipe(
        filter(v => v),
        switchMap(() => this.regulationsService.remove(regulation.Key))
      )
      .subscribe(
        (res: { statusCode: number }) => {
          if (res.statusCode === 200) {
            this.toastr.success(
              this.translate.instant('REGULATIONS.COMPONENT.FILE') +
                ' ' +
                regulation.name +
                ' ' +
                this.translate.instant('REGULATIONS.COMPONENT.REMOVED')
            );
            this.loadData(this.directory, this.fileName);
          }
        },
        () =>
          this.toastr.error(
            this.translate.instant('REGULATIONS.COMPONENT.FILE_MAYBE_MISSING'),
            this.translate.instant('REGULATIONS.COMPONENT.FILE_DELETE_ERROR')
          )
      );
  }

  deleteManyRegulations() {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'REGULATIONS.DELETE_MANY_DIALOG.TITLE'
        }
      })
      .afterClosed()
      .pipe(
        filter(v => v),
        switchMap(() => {
          const regulationsKey = this.selectedRegulations.map(regulation => {
            return regulation.Key;
          });

          return this.regulationsService.removeManyFiles(regulationsKey);
        })
      )
      .subscribe(
        () => {
          this.toastr.success(this.translate.instant('REGULATIONS.COMPONENT.FILES_DELETE_SUCCESS'));
          this.loadData(this.directory, this.fileName);
          this.selectedRegulations = [];
        },
        () => this.toastr.error(this.translate.instant('REGULATIONS.COMPONENT.FILES_DELETE_ERROR'))
      );
  }

  downloadFile(regulation: RegulationIface): void {
    this.regulationsService.findById(regulation.Key).subscribe(
      file => {
        if (file.hasOwnProperty('data')) {
          const { data, type } = file;
          const blob = RegulationsService.b64toBlob(Buffer.from(data).toString('base64'), type);
          FileSaver.saveAs(blob, regulation.Key, true);
          this.toastr.success(this.translate.instant('REGULATIONS.COMPONENT.FILE_DOWNLOADED'));
        }
      },
      err => {
        if (err.status === 404) {
          this.toastr.error(
            this.translate.instant('REGULATIONS.COMPONENT.FILE_MISSING'),
            this.translate.instant('REGULATIONS.COMPONENT.WARNING')
          );
        }
      }
    );
  }

  getUploadConfig(): object {
    return {
      class: 'my-filepond',
      name: 'file',
      multiple: true,
      acceptedFileTypes: ['application/pdf'],
      maxFileSize: '2MB',
      labelIdle: this.translate.instant('REGULATIONS.COMPONENT.POND_LABELS.DROP_FILE_HERE'),
      labelFileProcessing: this.translate.instant('REGULATIONS.COMPONENT.POND_LABELS.PROCESSING'),
      labelFileProcessingComplete: this.translate.instant('REGULATIONS.COMPONENT.POND_LABELS.PROCESSING_COMPLETE'),
      labelFileProcessingAborted: this.translate.instant('REGULATIONS.COMPONENT.POND_LABELS.PROCESSING_ABORTED'),
      labelFileProcessingError: this.translate.instant('REGULATIONS.COMPONENT.POND_LABELS.FILE_PROCESSING_ERROR'),
      labelTapToCancel: this.translate.instant('REGULATIONS.COMPONENT.POND_LABELS.TAP_TO_CANCEL'),
      labelTapToUndo: this.translate.instant('REGULATIONS.COMPONENT.POND_LABELS.TAP_TO_UNDO'),
      labelTapToRetry: this.translate.instant('REGULATIONS.COMPONENT.POND_LABELS.TAP_TO_RETRY'),
      labelFileTypeNotAllowed: this.translate.instant('REGULATIONS.COMPONENT.POND_LABELS.INVALID_TYPE'),
      labelMaxFileSize: this.translate.instant('REGULATIONS.COMPONENT.POND_LABELS.MAX_FILE_SIZE'),
      labelMaxFileSizeExceeded: this.translate.instant('REGULATIONS.COMPONENT.POND_LABELS.FILE_TO_BIG'),
      fileValidateTypeLabelExpectedTypes: this.translate.instant('REGULATIONS.COMPONENT.POND_LABELS.EXPECT'),
      allowRevert: false,
      server: {
        url: environment.FILES_API_URL,
        process: {
          url: `?directory=regulations`,
          headers: {
            Authorization: this.authService.token,
            'X-XSRF-TOKEN': this.cookieService.get('XSRF-TOKEN')
          },
          withCredentials: true,
          onload: () => {
            this.regulationsService.directorySubject.next(this.regulationDirectoryName);
            this.loadData(this.directory, this.fileName);
            if (this.isArchiveDisplay) {
              this.toggl.toggle();
              this.isArchiveDisplay = false;
              console.log(this.selectedRegulations);
              this.selectedRegulations = [];
            }
          },
          onerror: res => console.log(res)
        }
      }
    };
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/regulations/regulations.component.ts